import { SelectionList, Text } from '@dixa/lib-shared-habla';
import type { CustomAttributeFilterControl } from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback } from 'react';

import { CustomFieldTextAttribute } from 'apis/customFields';
import { useAnalyticsState } from 'app/views/layout/views/analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';

import {
  AnalyticsFilterChangeCallback,
  AnalyticsFilterLayout,
  AnalyticsFilterOptions,
} from '../../../../../filter';
import { customAttributesFilterAssets } from '../../utils/customAttributesFilterAssets';

type AnalyticsCustomAttributesFilterTextProps = {
  /** The filter field. */
  field: CustomAttributeFilterControl['field'];
  /** The custom field select attribute. */
  attribute: CustomFieldTextAttribute;
  /** The default selected operator. */
  defaultOperator?: CustomAttributeFilterControl['operator'];
  /** The currently selected operator. */
  selectedOperator?: CustomAttributeFilterControl['operator'];
  /** Callback when the filter is changed. */
  onChange: AnalyticsFilterChangeCallback<CustomAttributeFilterControl>;
  /** Callback for going back to the list of custom attributes. */
  onBack: VoidFunction;
};

const { operators } = customAttributesFilterAssets['text'];

export const AnalyticsCustomAttributesFilterText = memo(
  function AnalyticsCustomAttributesFilterText({
    field,
    attribute,
    defaultOperator,
    selectedOperator,
    onChange,
    onBack,
  }: AnalyticsCustomAttributesFilterTextProps): JSX.Element {
    const { dashboard } = useAnalyticsState();
    const trackingType = `${field}/${attribute.type}`;

    /**
     * Handles changing the filter operator.
     *
     * If filter is restored to its default state, the filter is removed from
     * the state.
     */
    const handleChangeOperator = useCallback(
      (operator: CustomAttributeFilterControl['operator']) => {
        onChange({
          op: 'upsert',
          filter: {
            field,
            values: [],
            operator,
            attribute_id: attribute.id,
          },
        });

        ampli.analyticsFilterOperatorUpdated({
          dashboard: dashboard.title,
          type: trackingType,
          operator,
        });
      },
      [attribute.id, dashboard.title, field, onChange, trackingType],
    );

    /** Handles remove the filter from the state. */
    const handleClear = useCallback(() => {
      if (!selectedOperator) return;

      onChange({
        op: 'remove',
        filter: {
          field,
          values: [],
          operator: selectedOperator,
          attribute_id: attribute.id,
        },
      });

      ampli.analyticsFilterCleared({
        dashboard: dashboard.title,
        type: trackingType,
      });
    }, [
      attribute.id,
      dashboard.title,
      field,
      onChange,
      selectedOperator,
      trackingType,
    ]);

    return (
      <>
        <AnalyticsFilterLayout.Row>
          <Text.Paragraph variant="3" subdued>
            {attribute.label}
          </Text.Paragraph>
        </AnalyticsFilterLayout.Row>

        <AnalyticsFilterLayout.Row noPadding>
          {/* Wrapping `BackItem` in an inactive `SelectionList` enables hover state. */}
          <SelectionList isActive={false}>
            <SelectionList.BackItem onSelected={onBack} />
          </SelectionList>
        </AnalyticsFilterLayout.Row>

        <AnalyticsFilterLayout.Row borderTop slottable>
          <AnalyticsFilterOptions
            operators={operators}
            defaultOperator={defaultOperator}
            selectedOperator={selectedOperator}
            isMissingSelectedValues
            isDropdownOpen
            isDefaultOpen
            onChangeOperator={handleChangeOperator}
            onClear={handleClear}
          />
        </AnalyticsFilterLayout.Row>
      </>
    );
  },
);
